import { useEffect, useState } from "react";
import { motion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];
const EXIT_EASE = [0.76, 0, 0.24, 1];
const LETTERS = "DIGITAL CENTAUR".split("").map((ch, i) => ({ ch, key: `${ch}-${i}` }));
const COLUMNS = [0, 1, 2, 3, 4];
const SEEN_KEY = "dc_loader_seen";
const TEAL = "#00C2B8";

const Columns = ({ exiting, baseDelay = 0.12, stagger = 0.06, duration = 0.75 }) => (
  <>
    {COLUMNS.map((i) => (
      <motion.div
        key={i}
        className="absolute top-0 h-full"
        style={{ left: `${i * 20}%`, width: "20.5%", background: "#060B14" }}
        initial={{ y: 0 }}
        animate={exiting ? { y: "-101%" } : { y: 0 }}
        transition={{ duration, delay: baseDelay + i * stagger, ease: EXIT_EASE }}
      />
    ))}
  </>
);

const QuickOverlay = ({ exiting }) => (
  <div className="fixed inset-0 z-[100] overflow-hidden" data-testid="site-preloader-quick">
    <Columns exiting={exiting} baseDelay={0.08} stagger={0.05} duration={0.65} />
    <motion.div
      className="absolute inset-0 flex items-center justify-center"
      animate={exiting ? { opacity: 0, scale: 1.15 } : { opacity: 1, scale: 1 }}
      transition={{ duration: 0.35, ease: EXIT_EASE }}
    >
      <motion.img
        src="/dc-logo.png"
        alt="Digital Centaur"
        className="w-16 h-16 md:w-20 md:h-20 object-contain"
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: EASE }}
      />
    </motion.div>
  </div>
);

const LogoEmblem = () => (
  <div className="relative flex items-center justify-center" style={{ width: 190, height: 190 }}>
    <motion.div
      className="absolute inset-[-70px] rounded-full"
      style={{ background: "radial-gradient(circle, rgba(0,194,184,0.28) 0%, rgba(0,194,184,0) 62%)" }}
      initial={{ opacity: 0, scale: 0.6 }}
      animate={{ opacity: [0, 0.9, 0.55], scale: [0.6, 1.15, 1] }}
      transition={{ duration: 1.8, delay: 0.35, ease: "easeOut" }}
    />
    <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full -rotate-90">
      <circle cx="50" cy="50" r="47" fill="none" stroke="rgba(0,194,184,0.14)" strokeWidth="0.8" />
      <motion.circle
        cx="50"
        cy="50"
        r="47"
        fill="none"
        stroke={TEAL}
        strokeWidth="1.4"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1.6, delay: 0.15, ease: EASE }}
      />
    </svg>
    <motion.svg
      viewBox="0 0 100 100"
      className="absolute inset-[-14px]"
      style={{ width: "calc(100% + 28px)", height: "calc(100% + 28px)" }}
      initial={{ rotate: 0, opacity: 0 }}
      animate={{ rotate: 360, opacity: 1 }}
      transition={{
        rotate: { duration: 6, repeat: Infinity, ease: "linear" },
        opacity: { duration: 0.6, delay: 0.5 },
      }}
    >
      <circle
        cx="50"
        cy="50"
        r="48"
        fill="none"
        stroke="rgba(0,194,184,0.45)"
        strokeWidth="0.5"
        strokeDasharray="1 11"
        strokeLinecap="round"
      />
    </motion.svg>
    <motion.img
      src="/dc-logo.png"
      alt="Digital Centaur"
      className="relative w-24 h-24 md:w-28 md:h-28 object-contain"
      initial={{ opacity: 0, scale: 0.35, rotate: -100, filter: "blur(12px)" }}
      animate={{ opacity: 1, scale: 1, rotate: 0, filter: "blur(0px)" }}
      transition={{ duration: 1.25, delay: 0.2, ease: EASE }}
    />
  </div>
);

const Wordmark = () => (
  <div className="mt-10 flex overflow-hidden" aria-label="Digital Centaur">
    {LETTERS.map(({ ch, key }, i) => (
      <motion.span
        key={key}
        className="font-mono text-[11px] md:text-xs tracking-[0.42em]"
        style={{ color: ch === " " ? "transparent" : TEAL, minWidth: ch === " " ? "0.8em" : undefined }}
        initial={{ y: 22, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.85 + i * 0.035, ease: EASE }}
      >
        {ch}
      </motion.span>
    ))}
  </div>
);

const FullOverlay = ({ exiting, count }) => (
  <div className="fixed inset-0 z-[100] overflow-hidden" data-testid="site-preloader">
    <Columns exiting={exiting} />
    <motion.div
      className="absolute inset-0 flex flex-col items-center justify-center"
      animate={exiting ? { opacity: 0, scale: 1.12, filter: "blur(8px)" } : { opacity: 1, scale: 1 }}
      transition={{ duration: 0.45, ease: EXIT_EASE }}
    >
      <LogoEmblem />
      <Wordmark />
      <motion.div
        className="mt-6 h-px w-40 origin-left"
        style={{ background: "linear-gradient(90deg, #00C2B8, rgba(0,194,184,0.1))" }}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: count / 100 }}
        transition={{ duration: 0.2, ease: "linear" }}
      />
    </motion.div>

    <motion.div
      className="absolute bottom-8 left-6 md:left-12 lg:left-24 font-mono tabular-nums"
      style={{ color: TEAL, fontSize: "clamp(2.5rem, 6vw, 5rem)", fontWeight: 300, lineHeight: 1 }}
      initial={{ opacity: 0, y: 20 }}
      animate={exiting ? { opacity: 0, y: -30 } : { opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: EASE }}
      data-testid="preloader-counter"
    >
      {String(count).padStart(3, "0")}
      <span className="text-sm align-top ml-1 opacity-60">%</span>
    </motion.div>

    <motion.div
      className="absolute bottom-10 right-6 md:right-12 lg:right-24 font-mono text-[10px] tracking-[0.32em] uppercase"
      style={{ color: "rgba(255,255,255,0.4)" }}
      initial={{ opacity: 0 }}
      animate={exiting ? { opacity: 0 } : { opacity: 1 }}
      transition={{ duration: 0.6, delay: 0.4 }}
    >
      Performance · Precision · Scale
    </motion.div>
  </div>
);

export const Preloader = () => {
  const [quick] = useState(() => sessionStorage.getItem(SEEN_KEY) === "1");
  const [phase, setPhase] = useState("intro");
  const [count, setCount] = useState(0);
  const introMs = quick ? 700 : 2150;
  const doneMs = quick ? 1500 : 3050;

  useEffect(() => {
    sessionStorage.setItem(SEEN_KEY, "1");
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setPhase("done");
      return;
    }
    document.body.style.overflow = "hidden";

    let rafId;
    if (!quick) {
      const start = performance.now();
      const tick = (now) => {
        const p = Math.min((now - start) / (introMs - 250), 1);
        setCount(Math.round(100 * (1 - Math.pow(1 - p, 3))));
        if (p < 1) rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    }

    const t1 = setTimeout(() => setPhase("exit"), introMs);
    const t2 = setTimeout(() => {
      setPhase("done");
      document.body.style.overflow = "";
    }, doneMs);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(t1);
      clearTimeout(t2);
      document.body.style.overflow = "";
    };
  }, [quick, introMs, doneMs]);

  if (phase === "done") return null;
  const exiting = phase === "exit";

  return quick ? <QuickOverlay exiting={exiting} /> : <FullOverlay exiting={exiting} count={count} />;
};

export default Preloader;
